import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Check, ChevronLeft } from "lucide-react";
import { getPublicPlans, subscribe, type SubscriptionPlan } from "../../services/subscriptionService";

type RegistrationPreview = {
  hospital: {
    hospitalName?: string;
    registrationNumber?: string;
    domainName?: string;
    officialEmail?: string;
    officialPhone?: string;
    addressline1?: string;
    addressline2?: string;
    city?: string;
    country?: string;
    postcode?: string;
  };
  admin: {
    name?: string;
    email?: string;
  };
};

const HospitalSubscriptionPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<RegistrationPreview | null>(null);
  const [step, setStep] = useState<"selection" | "preview">("selection");

  useEffect(() => {
    const storedPreview = localStorage.getItem("hospital_registration_preview");
    if (storedPreview) {
      try {
        setPreviewData(JSON.parse(storedPreview));
      } catch {
        setPreviewData(null);
      }
    }

    const load = async () => {
      setLoading(true);
      try {
        const data = await getPublicPlans();
        const list = Array.isArray(data) ? data : [];
        setPlans(list);
        const popular = list.find((p) => p.planTag === "Most Popular");
        setSelectedPlanId(popular?.id ?? list[0]?.id ?? null);
      } catch {
        toast.error("Failed to load plans");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  const [subscribingId, setSubscribingId] = useState<string | null>(null);

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlanId(plan.id);
    setStep("preview");
  };

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    try {
      setSubscribingId(plan.id);

      const customToken = localStorage.getItem("temp_subscription_token") || undefined;

      const res = await subscribe(plan.id, customToken);
      const tenantUrl = res.data.tenantUrl;

      if (res.data?.success && tenantUrl) {
        localStorage.removeItem("temp_subscription_token");
        localStorage.removeItem("hospital_registration_preview");
        toast.success(`Successfully subscribed to ${plan.planName}! Redirecting...`, { duration: 5000 });
        if (res.data.welcomeEmailSent === false) {
          toast.error("Subscribed successfully, but the welcome email could not be sent. Please share login details manually.", { duration: 6000 });
        }

        setTimeout(() => {
          window.location.href = tenantUrl;
        }, 3000);
      } else {
        toast.error("Subscription succeeded, but no tenant URL was returned.");
        navigate("/login");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to subscribe");
    } finally {
      setSubscribingId(null);
    }
  };

  const PlanCard = ({
    plan,
    isSelected,
    onSelect,
    loading,
  }: {
    plan: SubscriptionPlan;
    isSelected: boolean;
    onSelect: () => void;
    loading: boolean;
  }) => {
    const price = `${plan.price}${plan.currency && plan.currency !== "USD" ? ` ${plan.currency}` : ""}`;
    const billing = `${plan.durationValue} ${plan.durationType}`;

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
        className={`
          flex h-full min-h-[420px] w-full max-w-[440px] flex-col rounded-2xl border
          bg-[#f3f3f3] px-5 py-5 shadow-sm transition-all duration-200 mx-auto
          ${isSelected
            ? "border-[#61c7c1] ring-2 ring-[#61c7c1]/30 shadow-md"
            : "border-[#cfd9d9] hover:border-[#6bbec7] hover:shadow-md"
          }
        `}
      >
        <div className="mb-4 flex justify-end">
          {plan.planTag ? (
            <span className="inline-flex rounded-full border border-transparent bg-transparent px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
              {plan.planTag}
            </span>
          ) : null}
        </div>

        <div className="mb-3 text-left">
          <h2 className="text-[15px] font-bold uppercase tracking-[0.18em] text-gray-800">
            {plan.planName}
          </h2>
        </div>

        <div className="mb-4 flex items-end gap-2">
          <span className="text-[30px] font-black leading-none text-gray-900">{price}</span>
          <span className="pb-1 text-[12px] text-gray-500">/ {billing}</span>
        </div>

        <div className="mb-4 border-t border-neutral-200" />

        {plan.includedFeatures && plan.includedFeatures.length > 0 ? (
          <ul className="flex-1 space-y-3 text-[13px] text-gray-700">
            {plan.includedFeatures.map((feature, index) => (
              <li key={`${plan.id}-${index}`} className="flex items-center gap-3">
                <span className="flex h-4 w-4 items-center justify-center text-[#5bc2bd]">
                  <Check size={15} strokeWidth={2.5} />
                </span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex-1 text-sm text-gray-500">Included features will appear here.</div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          disabled={loading}
          className={`mt-5 w-full rounded-md px-4 py-3 text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6cc7c1]/50 ${
            isSelected
              ? "bg-[#2a9d8f] text-white shadow-[0_6px_18px_rgba(42,157,143,0.3)] hover:bg-[#21867a]"
              : "bg-[#3b827a] text-white shadow-[0_6px_18px_rgba(59,130,122,0.25)] hover:bg-[#4da39b] hover:shadow-[0_8px_22px_rgba(77,163,155,0.35)]"
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {loading ? "Preparing..." : isSelected ? "Review Selected" : "Select Plan"}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full bg-transparent px-3 py-4 sm:px-5 lg:px-8">
      <div className="mx-auto flex w-full max-w-[1500px] flex-col min-h-[calc(100vh-2rem)]">
        <div className="mb-4 flex items-center justify-start rounded-lg border border-transparent bg-transparent px-1 py-2">
          <button
            type="button"
            onClick={() => (step === "preview" ? setStep("selection") : navigate(-1))}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 transition hover:text-gray-900"
          >
            <ChevronLeft size={18} />
            <span>{step === "preview" ? "Back" : "Back"}</span>
          </button>
        </div>

        <div className="flex flex-1 flex-col justify-center">
          {step === "selection" ? (
            <>
              <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-[3.25rem]">
                  Choose Your Subscription Plan
                </h1>
                <p className="mt-2 text-sm text-gray-500 sm:text-base">
                  Select a plan that fits your practice. You will review the details before finalizing.
                </p>
              </div>

              {loading ? (
                <div className="flex min-h-[260px] items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500" />
                </div>
              ) : (
                <div className={`grid w-full justify-center gap-5 ${plans.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"}`}>
                  {plans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      isSelected={selectedPlanId === plan.id}
                      onSelect={() => handleSelectPlan(plan)}
                      loading={subscribingId === plan.id}
                    />
                  ))}
                </div>
              )}

              {!loading && plans.length === 0 && (
                <p className="py-8 text-center text-sm text-gray-500">No plans available at the moment.</p>
              )}
            </>
          ) : (
            selectedPlan && (
              <div className="mx-auto w-full max-w-6xl">
                <div className="mb-6 text-center">
                  <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                    Review Your Subscription
                  </h1>
                  <p className="mt-2 text-sm text-gray-500">
                    Confirm your hospital details and selected plan before proceeding.
                  </p>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1.3fr_0.9fr]">
                  <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                    <h2 className="mb-4 text-lg font-bold uppercase tracking-[0.12em] text-gray-700">
                      Hospital Details
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <DetailRow label="Hospital Name" value={previewData?.hospital.hospitalName || "-"} />
                      <DetailRow label="Domain" value={previewData?.hospital.domainName || "-"} />
                      <DetailRow label="Registration No." value={previewData?.hospital.registrationNumber || "-"} />
                      <DetailRow label="Official Email" value={previewData?.hospital.officialEmail || "-"} />
                      <DetailRow label="Phone" value={previewData?.hospital.officialPhone || "-"} />
                      <DetailRow label="Admin Name" value={previewData?.admin.name || "-"} />
                      <DetailRow label="Admin Email" value={previewData?.admin.email || "-"} />
                      <DetailRow label="Address" value={[previewData?.hospital.addressline1, previewData?.hospital.addressline2].filter(Boolean).join(", ") || "-"} />
                      <DetailRow label="City" value={previewData?.hospital.city || "-"} />
                      <DetailRow label="Country" value={previewData?.hospital.country || "-"} />
                      <DetailRow label="Postcode" value={previewData?.hospital.postcode || "-"} />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#7adad3] bg-[#f3fdfc] p-6 shadow-sm">
                    <h2 className="mb-4 text-lg font-bold uppercase tracking-[0.12em] text-[#0d766e]">
                      Selected Plan
                    </h2>

                    <div className="mb-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                        {selectedPlan.planTag || "Plan"}
                      </p>
                      <h3 className="mt-2 text-2xl font-black text-gray-900">{selectedPlan.planName}</h3>
                    </div>

                    <div className="mb-4 flex items-end gap-2">
                      <span className="text-4xl font-black text-gray-900">
                        {selectedPlan.price}{selectedPlan.currency && selectedPlan.currency !== "USD" ? ` ${selectedPlan.currency}` : ""}
                      </span>
                      <span className="pb-2 text-sm text-gray-500">/ {selectedPlan.durationValue} {selectedPlan.durationType}</span>
                    </div>

                    <ul className="space-y-2 text-sm text-gray-700">
                      {(selectedPlan.includedFeatures || []).map((feature, index) => (
                        <li key={`${selectedPlan.id}-${index}`} className="flex items-center gap-2">
                          <span className="text-[#0f766e]">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => handleSubscribe(selectedPlan)}
                      disabled={subscribingId === selectedPlan.id}
                      className="mt-6 w-full rounded-xl bg-[#2a9d8f] px-4 py-3 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(42,157,143,0.3)] transition hover:bg-[#21867a] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {subscribingId === selectedPlan.id ? "Proceeding..." : "Proceed"}
                    </button>

                    <button
                      type="button"
                      onClick={() => setStep("selection")}
                      className="mt-3 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:text-gray-900"
                    >
                      Change Plan
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500">{label}</p>
    <p className="mt-1 text-sm font-medium text-gray-800 break-words">{value}</p>
  </div>
);

export default HospitalSubscriptionPage;
